"use client"

import styled from "@emotion/styled"
import { getUmbracoMediaUrl } from "@eurodrive/utils/getUmbracoMediaUrl"
import { branchLocatorClicked } from "@eurodrive/utils/gtm"
import { Typography } from "@mui/material"
import { BranchDto } from "api"
import Image from "next/image"

const StyledLayout = styled.div`
  display: flex;
  height: 100%;
  padding-bottom: 16px;
  border-radius: 8px;
  border: 1px solid #ece9e9;
  background-color: white;
  overflow: hidden;
  box-sizing: border-box;
`

const InnerCard = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 12px;
`

const ImageContainer = styled.div`
  width: 100%;
  height: 174px;
  position: relative;
`

const ContentBox = styled.div`
  display: flex;
  padding: 0px 16px;
  flex-direction: column;
  gap: 8px;
`

const DetailsBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const DetailsRow = styled.div`
  display: flex;
  gap: 8px;
  /* align-items: center; */
`

const TimeColumn = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledLink = styled.a`
  text-decoration: none;
  display: flex;
  width: fit-content;
  :focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
`
const StyledTypography = styled(Typography)`
  text-decoration: underline;
`
function getTime(time: string) {
  const [hours, minutes] = time.split(":")
  const formattedTime = `${hours}.${minutes}` // "20.00"

  return formattedTime
}

export default function BranchCard({
  branch,
  desktopImage,
  mobileImage,
}: {
  branch: BranchDto
  desktopImage?: string
  mobileImage?: string
}) {
  // Detect mobile device on client side (check for "Mobi" in user agent per MDN recommendation)
  const isMobileDevice =
    typeof window !== "undefined" && /Mobi/i.test(navigator.userAgent)

  // Use coordinates for precise location if available, otherwise fall back to address
  const mapLink =
    branch.latitude && branch.longitude
      ? `https://maps.google.com/?q=${branch.latitude},${branch.longitude}`
      : `https://maps.google.com/?q=${encodeURIComponent(
          `${branch.street} ${branch.houseNumber}, ${branch.city}`,
        )}`

  // Use branch page link for desktop/tablets, map link only for actual mobile devices
  const addressHref = isMobileDevice
    ? mapLink
    : `/branches?branchId=${branch.id}`

  // Empty when the branch has no Umbraco thumbnail, which happens on cultures with
  // no published branch nodes. An Image with an empty src renders the browser's
  // broken-image glyph, so the container is dropped instead (END-288).
  const imageUrl = getUmbracoMediaUrl(mobileImage || desktopImage || "")

  return (
    <StyledLayout>
      <InnerCard>
        {imageUrl && (
          <ImageContainer>
            <Image
              fill
              src={imageUrl}
              alt={branch.branchName}
              sizes="(min-width: 900px) 33vw, 100vw"
              style={{ objectFit: "cover" }}
            />
          </ImageContainer>
        )}
        <ContentBox>
          <Typography variant="h3">{branch.branchName}</Typography>
          <DetailsBox>
            <DetailsRow
              onClick={() =>
                branchLocatorClicked(branch.branchName, "branch_card_address")
              }
            >
              <DetailsRow>
                <Image
                  src="/icons/map.svg"
                  alt="מיקום"
                  width={18}
                  height={18}
                />
                <StyledLink
                  href={addressHref}
                  target={isMobileDevice ? "_blank" : undefined}
                >
                  <StyledTypography variant="body2">
                    {branch.street} {branch.houseNumber}, {branch.city}
                  </StyledTypography>
                </StyledLink>
              </DetailsRow>
            </DetailsRow>
            <DetailsRow>
              <Image
                src="/icons/clock.svg"
                alt="שעות פעילות"
                width={18}
                height={18}
              />
              <TimeColumn>
                <Typography variant="body2">
                  א׳ - ה׳: {getTime(branch.regularDayOpeningHours)} -{" "}
                  {getTime(branch.regularDayClosingHours)}
                </Typography>
                <Typography variant="body2">
                  ו׳: {getTime(branch.holidayDayOpeningHours)} -{" "}
                  {getTime(branch.holidayDayClosingHours)}
                </Typography>
              </TimeColumn>
            </DetailsRow>
            <DetailsRow>
              <Image
                src="/icons/email.svg"
                alt="אימייל"
                width={18}
                height={18}
              />
              <StyledLink
                href={`mailto:${branch.emailAddress}`}
                onClick={() =>
                  branchLocatorClicked(branch.branchName, "branches_page")
                }
              >
                <Typography variant="body3">{branch.emailAddress}</Typography>
              </StyledLink>
            </DetailsRow>
            <DetailsRow>
              <Image
                src="/icons/phone.svg"
                alt="טלפון"
                width={18}
                height={18}
              />
              <StyledLink
                href={`tel:${branch.phoneNumbers[0]}`}
                onClick={() =>
                  branchLocatorClicked(branch.branchName, "branches_page")
                }
              >
                <Typography variant="body3">
                  {branch.phoneNumbers[0]}
                </Typography>
              </StyledLink>
            </DetailsRow>
          </DetailsBox>
        </ContentBox>
      </InnerCard>
    </StyledLayout>
  )
}
